// The single reducer. Pure: (state, action, content) → Result<{state, outbound}, error>.
// Every action that can fail returns a typed error — the reducer never throws for
// game-rule violations. Throws are reserved for impossible config states.

import type { BuilderContent } from '../config/types.js';
import type { GameState, OutboundEvent } from '../state/types.js';
import type { Action } from './types.js';
import { ok, fail, type ApplyResult } from './result.js';
import { applyTxn } from '../ledger/append.js';
import { findAsset, findBusiness, findLiability } from '../config/load.js';
import { buyAsset } from '../assets/buy.js';
import { buyLiability } from '../liabilities/buy.js';
import { takeLoan } from '../loans/take.js';
import { deposit, withdraw } from '../savings/deposit.js';
import { refillAP } from '../time/ap.js';
import { settleWeek } from '../time/settle.js';
import { cashflowForWeek } from '../ledger/selectors.js';
import { daysBetween } from '../time/clock.js';
import { applyConsequenceEffect } from '../decisions/consequences.js';
import { businessAssetReadiness } from '../business/asAsset.js';
import { maxServiceHours } from '../ventures/services.js';
import { POST_ENERGY } from '../ventures/social.js';
import { PRACTICE_ENERGY, TOURNAMENT_ENTRY, type GamingAction } from '../ventures/gaming.js';
import {
  generateMarket,
  marketCondition,
  resellValue,
  MAX_INVENTORY,
} from '../ventures/trade.js';
import {
  generateListings,
  propertyValue,
  renovationCost,
  saleValue,
  MAX_PROPERTIES,
  MAX_RENOVATIONS,
} from '../ventures/property.js';
import { generateDeals, WORK_ENERGY } from '../ventures/broker.js';

// ---------- per-action handlers --------------------------------------------

function startBusiness(state: GameState, defId: string, content: BuilderContent): ApplyResult {
  const def = findBusiness(content, defId);
  if (!def) return fail('UNKNOWN_BUSINESS', `No business with id "${defId}"`);

  if (state.businesses.some((b) => b.defId === defId)) {
    return fail('ALREADY_STARTED', `Already running a ${def.name}`);
  }
  if (state.wallet.cash < def.equipmentCost) {
    return fail(
      'INSUFFICIENT_CASH',
      `Need B$${def.equipmentCost} for ${def.name} equipment, have B$${state.wallet.cash.toFixed(2)}.`,
    );
  }

  // Charge equipment as a capital purchase. We also record it as an Asset on the balance
  // sheet (with weeklyYield 0) so net-worth reflects what was bought.
  let s = applyTxn(state, {
    type: 'asset_buy',
    amount: -def.equipmentCost,
    category: 'capital',
    note: `Equipment — ${def.name}`,
  });

  const instanceId = `biz-${state.businesses.length + 1}`;
  const equipmentAssetId = `equip-${state.assets.length + 1}`;
  s = {
    ...s,
    assets: [
      ...s.assets,
      {
        id: equipmentAssetId,
        defId: `equipment:${def.id}`,
        costPaid: def.equipmentCost,
        weeklyYield: 0,
        weeksOwned: 0,
      },
    ],
    businesses: [
      ...s.businesses,
      {
        id: instanceId,
        defId: def.id,
        price: def.sweetPrice,
        marketingSpend: 0,
        weeksOperated: 0,
      },
    ],
  };

  return ok(s, [{ kind: 'IslandUnlocked', islandId: instanceId }]);
}

function setPrice(state: GameState, businessId: string, price: number): ApplyResult {
  if (price < 0) return fail('INVALID_PRICE', 'Price must be ≥ 0.');
  const idx = state.businesses.findIndex((b) => b.id === businessId);
  if (idx < 0) return fail('UNKNOWN_BUSINESS', `No business instance "${businessId}"`);
  const updated = [...state.businesses];
  updated[idx] = { ...updated[idx]!, price };
  return ok({ ...state, businesses: updated });
}

function spendMarketing(state: GameState, businessId: string, amount: number): ApplyResult {
  if (amount < 0) return fail('INVALID_MARKETING', 'Marketing budget must be ≥ 0.');
  const idx = state.businesses.findIndex((b) => b.id === businessId);
  if (idx < 0) return fail('UNKNOWN_BUSINESS', `No business instance "${businessId}"`);
  const updated = [...state.businesses];
  updated[idx] = { ...updated[idx]!, marketingSpend: amount };
  return ok({ ...state, businesses: updated });
}

/** "Business as Asset" — convert a business that's been profitable for the last
 *  three settled weeks into a passive lifecycle asset. Marks the instance and
 *  emits BusinessRegisteredAsAsset; the UI is the bridge that adds the asset to
 *  the lifecycle store (engine + life are separate by design). */
function registerBusinessAsAsset(
  state: GameState,
  businessId: string,
  content: BuilderContent,
): ApplyResult {
  const idx = state.businesses.findIndex((b) => b.id === businessId);
  if (idx < 0) return fail('UNKNOWN_BUSINESS', `No business instance "${businessId}"`);
  const inst = state.businesses[idx]!;
  if (inst.registeredAsAsset) {
    return fail('ALREADY_REGISTERED', 'This business is already an asset.');
  }
  const readiness = businessAssetReadiness(state, businessId);
  if (!readiness.ready) {
    return fail(
      'INSUFFICIENT_STREAK',
      'Need three profitable weeks in a row before this business can become an asset.',
    );
  }
  const def = findBusiness(content, inst.defId);
  const name = def?.name ?? 'Business';

  const updated = [...state.businesses];
  updated[idx] = { ...inst, registeredAsAsset: true };
  const next: GameState = { ...state, businesses: updated };

  const avgWeekly = Math.round(
    readiness.recentWeeks.reduce((s, r) => s + r.profit, 0) / readiness.recentWeeks.length,
  );

  return ok(next, [
    {
      kind: 'BusinessRegisteredAsAsset',
      businessId,
      defId: inst.defId,
      name,
      monthlyIncome: readiness.monthlyIncome,
      value: readiness.value,
      avgWeeklyProfit: avgWeekly,
    },
  ]);
}

function buyAssetAction(state: GameState, defId: string, content: BuilderContent): ApplyResult {
  const def = findAsset(content, defId);
  if (!def) return fail('UNKNOWN_ASSET', `No asset with id "${defId}"`);
  const instanceId = `asset-${state.assets.length + 1}`;
  const outcome = buyAsset(state, def, instanceId);
  if (!outcome.ok) return fail(outcome.error.code, outcome.error.message);
  return ok(outcome.value.state, [
    { kind: 'CoinsSpent', amount: def.cost, reason: `Bought ${def.name}` },
    { kind: 'IslandUnlocked', islandId: instanceId },
  ]);
}

function buyLiabilityAction(
  state: GameState,
  defId: string,
  content: BuilderContent,
): ApplyResult {
  const def = findLiability(content, defId);
  if (!def) return fail('UNKNOWN_LIABILITY', `No liability with id "${defId}"`);
  const instanceId = `lia-${state.liabilities.length + 1}`;
  const outcome = buyLiability(state, def, instanceId);
  if (!outcome.ok) return fail(outcome.error.code, outcome.error.message);
  return ok(outcome.value.state, [
    { kind: 'CoinsSpent', amount: def.cost, reason: `Bought ${def.name}` },
  ]);
}

function takeLoanAction(
  state: GameState,
  args: { principal: number; termWeeks?: number },
  content: BuilderContent,
): ApplyResult {
  const loanId = `loan-${state.loans.length + 1}`;
  const outcome = takeLoan(state, { ...args, loanId }, content.tuning);
  if (!outcome.ok) return fail(outcome.error.code, outcome.error.message);
  return ok(outcome.value.state, [
    { kind: 'CoinsEarned', amount: args.principal, from: 'Loan' },
  ]);
}

function depositAction(state: GameState, amount: number): ApplyResult {
  const outcome = deposit(state, amount);
  if (!outcome.ok) return fail(outcome.error.code, outcome.error.message);
  return ok(outcome.value, [{ kind: 'CoinsSpent', amount, reason: 'Saved in jar' }]);
}

function withdrawAction(state: GameState, amount: number): ApplyResult {
  const outcome = withdraw(state, amount);
  if (!outcome.ok) return fail(outcome.error.code, outcome.error.message);
  return ok(outcome.value, [{ kind: 'CoinsEarned', amount, from: 'Savings withdrawal' }]);
}

function acceptInvestmentAction(
  state: GameState,
  amount: number,
  sharePct: number,
): ApplyResult {
  if (amount <= 0) return fail('NON_POSITIVE_AMOUNT', 'Investment must be > 0.');
  if (sharePct <= 0 || sharePct >= 1) {
    return fail('INVALID_SHARE', 'Investor share must be between 0% and 100%.');
  }
  const existing = state.investors ?? [];
  const next = applyTxn(state, {
    type: 'invest',
    amount: +amount,
    category: 'capital',
    note: `Investor capital — ${(sharePct * 100).toFixed(0)}% revenue share`,
  });
  const investor = {
    id: `inv-${existing.length + 1}`,
    capitalProvided: amount,
    sharePct,
    weekAccepted: state.clock.week,
  };
  return ok({ ...next, investors: [...existing, investor] }, [
    { kind: 'CoinsEarned', amount, from: 'Investor' },
  ]);
}

// Work↔fun balance tuning.
const GRIND_EDGE_ID = 'grind-edge';
const GRIND_EDGE_MULT = 1.12;
const GRIND_ENERGY_COST = 15; // happiness lost by sprinting
const BREAK_ENERGY_GAIN = 20; // happiness restored by resting
const BREAK_CASH_COST = 15; // what a fun break costs

function pushHardAction(state: GameState): ApplyResult {
  // A sprint: refresh a one-week demand edge, but pay for it in energy.
  const otherEffects = state.weeklyEffects.filter((e) => e.id !== GRIND_EDGE_ID);
  const happiness = Math.max(0, state.profile.happiness - GRIND_ENERGY_COST);
  return ok(
    {
      ...state,
      weeklyEffects: [
        ...otherEffects,
        {
          id: GRIND_EDGE_ID,
          weeksRemaining: 1,
          effects: [{ kind: 'demandMultiplier' as const, multiplier: GRIND_EDGE_MULT }],
        },
      ],
      profile: { ...state.profile, happiness },
    },
    [{ kind: 'LightWarmed', mindsetDelta: 0 }],
  );
}

function takeBreakAction(state: GameState): ApplyResult {
  if (state.wallet.cash < BREAK_CASH_COST) {
    return fail('INSUFFICIENT_CASH', `A break costs B$${BREAK_CASH_COST}.`);
  }
  const next = applyTxn(state, {
    type: 'fun',
    amount: -BREAK_CASH_COST,
    category: 'expense',
    note: 'Fun break — recharged',
  });
  const happiness = Math.min(100, next.profile.happiness + BREAK_ENERGY_GAIN);
  return ok(
    { ...next, profile: { ...next.profile, happiness } },
    [{ kind: 'CoinsSpent', amount: BREAK_CASH_COST, reason: 'Recharged' }],
  );
}

const clampStat = (x: number) => Math.min(100, Math.max(0, x));

function resolveDecisionAction(
  state: GameState,
  a: { energy?: number; reputation?: number; cashCost?: number; edgePct?: number },
): ApplyResult {
  let s = state;
  if (a.cashCost && a.cashCost > 0) {
    if (s.wallet.cash < a.cashCost) {
      return fail('INSUFFICIENT_CASH', `That choice costs B$${a.cashCost}.`);
    }
    s = applyTxn(s, { type: 'fun', amount: -a.cashCost, category: 'expense', note: 'Decision' });
  }
  const DECISION_EDGE_ID = 'decision-edge';
  const weeklyEffects = a.edgePct
    ? [
        ...s.weeklyEffects.filter((e) => e.id !== DECISION_EDGE_ID),
        {
          id: DECISION_EDGE_ID,
          weeksRemaining: 1,
          effects: [{ kind: 'demandMultiplier' as const, multiplier: 1 + a.edgePct / 100 }],
        },
      ]
    : s.weeklyEffects;
  return ok({
    ...s,
    weeklyEffects,
    lastDecisionWeek: s.clock.week,
    profile: {
      ...s.profile,
      happiness: clampStat(s.profile.happiness + (a.energy ?? 0)),
      reputation: clampStat(s.profile.reputation + (a.reputation ?? 0)),
    },
  });
}

function giveAction(state: GameState, amount: number, causeId: string): ApplyResult {
  if (amount <= 0) return fail('NON_POSITIVE_AMOUNT', 'Give must be > 0.');
  if (state.wallet.cash < amount) return fail('INSUFFICIENT_CASH', 'Not enough cash to give.');
  const next = applyTxn(state, {
    type: 'give',
    amount: -amount,
    category: 'expense',
    note: `Gave to ${causeId}`,
  });
  return ok(next, [{ kind: 'CoinsSpent', amount, reason: 'Generosity' }]);
}

// A growth-mindset morning earns a one-week "edge": a small lift to demand,
// modelling how showing up with focus/discipline pays off. The easy choice gets
// none. Tuned to feel meaningful without being game-breaking.
const MINDSET_EDGE_ID = 'mindset-focus';
const MINDSET_EDGE_MULT = 1.1;

function wakeUpAction(
  state: GameState,
  dayKey: string,
  mindset?: 'growth' | 'easy',
): ApplyResult {
  const streak = state.profile.streak;
  if (streak.lastWakeUpDayKey === dayKey) {
    // Already did the ritual today — no-op, no error.
    return ok(state);
  }
  // Consistency has stakes: a streak only survives if you show up the very next
  // day. Skip a day and it restarts at 1. The first-ever wake-up also starts at 1.
  const gap = streak.lastWakeUpDayKey ? daysBetween(streak.lastWakeUpDayKey, dayKey) : 0;
  const continues = gap === 1;
  const newStreak = continues ? streak.current + 1 : 1;
  const longest = Math.max(streak.longest, newStreak);

  // Refresh the mindset edge: drop any prior one, add a fresh week if 'growth'.
  const otherEffects = state.weeklyEffects.filter((e) => e.id !== MINDSET_EDGE_ID);
  const weeklyEffects =
    mindset === 'growth'
      ? [
          ...otherEffects,
          {
            id: MINDSET_EDGE_ID,
            weeksRemaining: 1,
            effects: [{ kind: 'demandMultiplier' as const, multiplier: MINDSET_EDGE_MULT }],
          },
        ]
      : otherEffects;

  return ok(
    {
      ...state,
      weeklyEffects,
      profile: {
        ...state.profile,
        streak: { current: newStreak, longest, lastWakeUpDayKey: dayKey },
      },
    },
    [{ kind: 'LightWarmed', mindsetDelta: 1 }],
  );
}

function reflectAction(
  state: GameState,
  entry: string,
  tags?: {
    loves?: string[];
    goodAt?: string[];
    worldNeeds?: string[];
    paidFor?: string[];
  },
): ApplyResult {
  void entry; // currently we only store the tags; the entry text is stored by the UI.
  const dedupe = (a: string[], b?: string[]): string[] =>
    Array.from(new Set([...a, ...(b ?? [])]));
  const updated = {
    ...state,
    profile: {
      ...state.profile,
      xp: state.profile.xp + 5, // each reflection earns a small chunk of XP
      ikigai: {
        lovesTags: dedupe(state.profile.ikigai.lovesTags, tags?.loves),
        goodAtTags: dedupe(state.profile.ikigai.goodAtTags, tags?.goodAt),
        worldNeedsTags: dedupe(state.profile.ikigai.worldNeedsTags, tags?.worldNeeds),
        paidForTags: dedupe(state.profile.ikigai.paidForTags, tags?.paidFor),
      },
    },
  };
  return ok(updated);
}

function resolveDecisionCardAction(
  state: GameState,
  action: {
    scheduleId: string;
    immediate?: { cash?: number; reputation?: number; demandMult?: number; demandWeeks?: number };
    delayed?: {
      weeksAhead: number;
      framework: string;
      lesson: string;
      effect: { cash?: number; reputation?: number; demandMult?: number; demandWeeks?: number };
    };
  },
): ApplyResult {
  // One card per week — guard against re-resolving the same week.
  if (state.lastDecisionWeek >= state.clock.week) {
    return ok(state);
  }
  let s = state;
  if (action.immediate) s = applyConsequenceEffect(s, action.immediate, 'now');

  const pending = action.delayed
    ? [
        ...s.pendingConsequences,
        {
          id: action.scheduleId,
          landsWeek: s.clock.week + Math.max(1, action.delayed.weeksAhead),
          framework: action.delayed.framework,
          lesson: action.delayed.lesson,
          effect: action.delayed.effect,
        },
      ]
    : s.pendingConsequences;

  return ok({ ...s, pendingConsequences: pending, lastDecisionWeek: s.clock.week });
}

function ackConsequenceAction(state: GameState, id: string): ApplyResult {
  return ok({ ...state, landedConsequences: state.landedConsequences.filter((l) => l.id !== id) });
}

// ---------- Ventures -------------------------------------------------------

function startServicesAction(state: GameState): ApplyResult {
  return ok({ ...state, services: { ...state.services, active: true } });
}
function setServiceRateAction(state: GameState, rate: number): ApplyResult {
  if (rate < 0) return fail('INVALID_RATE', 'Rate must be ≥ 0.');
  return ok({ ...state, services: { ...state.services, hourlyRate: Math.round(rate) } });
}
function setServiceHoursAction(state: GameState, hours: number): ApplyResult {
  // Hours you can plan are capped by your energy — the heart of the S-quadrant lesson.
  const capped = Math.max(0, Math.min(Math.round(hours), maxServiceHours(state.profile.happiness)));
  return ok({ ...state, services: { ...state.services, plannedHours: capped } });
}
function startSocialAction(state: GameState): ApplyResult {
  return ok({ ...state, social: { ...state.social, active: true } });
}
function postContentAction(state: GameState): ApplyResult {
  if (!state.social.active) return fail('NOT_STARTED', 'Start your channel first.');
  if (state.social.postedThisWeek) return ok(state); // already posted this week — no-op
  if (state.profile.happiness < POST_ENERGY) {
    return fail('LOW_ENERGY', 'Too tired to post well — take a break first.');
  }
  return ok({
    ...state,
    profile: { ...state.profile, happiness: Math.max(0, state.profile.happiness - POST_ENERGY) },
    social: { ...state.social, postedThisWeek: true },
  });
}

function startGamingAction(state: GameState): ApplyResult {
  return ok({ ...state, gaming: { ...state.gaming, active: true } });
}
function setGamingActionAction(state: GameState, action: GamingAction): ApplyResult {
  if (!state.gaming.active) return fail('NOT_STARTED', 'Start gaming first.');
  // Best-effort guards so the UI can warn early; settle re-checks too.
  if (action === 'practice' && state.profile.happiness < PRACTICE_ENERGY) {
    return fail('LOW_ENERGY', 'Too tired to practise well — take a break first.');
  }
  if (action === 'compete' && state.wallet.cash < TOURNAMENT_ENTRY) {
    return fail('INSUFFICIENT_FUNDS', `You need B$${TOURNAMENT_ENTRY} to enter a tournament.`);
  }
  return ok({ ...state, gaming: { ...state.gaming, weekAction: action } });
}

function startTradeAction(state: GameState): ApplyResult {
  return ok({ ...state, trade: { ...state.trade, active: true } });
}
function tradeBuyAction(state: GameState, slot: number): ApplyResult {
  if (!state.trade.active) return fail('NOT_STARTED', 'Start trading first.');
  if (state.trade.inventory.length >= MAX_INVENTORY) {
    return fail('INVENTORY_FULL', `You can only hold ${MAX_INVENTORY} items — sell one first.`);
  }
  const week = state.clock.week;
  const offers = generateMarket(state.rngSeed, week);
  const offer = offers.find((o) => o.slot === slot);
  if (!offer) return fail('NO_SUCH_OFFER', 'That item is not on the market this week.');

  const boughtThisWeek = state.trade.boughtSlotsWeek === week ? state.trade.boughtSlots : [];
  if (boughtThisWeek.includes(slot)) return fail('ALREADY_BOUGHT', 'You already bought that one.');
  if (state.wallet.cash < offer.buyPrice) {
    return fail('INSUFFICIENT_FUNDS', `You need B$${offer.buyPrice} to buy ${offer.name}.`);
  }

  let s = applyTxn(state, {
    type: 'trade_buy',
    amount: -offer.buyPrice,
    category: 'expense',
    note: `Bought ${offer.name} for B$${offer.buyPrice}`,
  });
  const item = {
    id: `${week}-${slot}`,
    defId: offer.defId,
    name: offer.name,
    emoji: offer.emoji,
    category: offer.category,
    buyPrice: offer.buyPrice,
    fairValue: offer.fairValue,
    predictedLow: offer.predictedLow,
    predictedHigh: offer.predictedHigh,
    boughtWeek: week,
  };
  s = {
    ...s,
    trade: {
      ...s.trade,
      inventory: [...s.trade.inventory, item],
      boughtSlots: [...boughtThisWeek, slot],
      boughtSlotsWeek: week,
    },
  };
  return ok(s, [{ kind: 'CoinsSpent', amount: offer.buyPrice, reason: `Bought ${offer.name}` }]);
}
function tradeSellAction(state: GameState, itemId: string): ApplyResult {
  if (!state.trade.active) return fail('NOT_STARTED', 'Start trading first.');
  const item = state.trade.inventory.find((i) => i.id === itemId);
  if (!item) return fail('NOT_FOUND', 'You are not holding that item.');
  const week = state.clock.week;
  // Patience rule: you must hold at least a week — no same-week flips.
  if (item.boughtWeek >= week) return fail('TOO_SOON', 'Hold it at least a week before selling.');

  const weeksHeld = week - item.boughtWeek;
  const condition = marketCondition(state.rngSeed, week);
  const value = resellValue(item, weeksHeld, condition);
  let s = applyTxn(state, {
    type: 'trade_sale',
    amount: value,
    category: 'active',
    note: `Sold ${item.name} for B$${value} (held ${weeksHeld}w)`,
  });
  s = { ...s, trade: { ...s.trade, inventory: s.trade.inventory.filter((i) => i.id !== itemId) } };
  return ok(s, [{ kind: 'CoinsEarned', amount: value, from: `Sold ${item.name}` }]);
}

// ---------- property ---------------------------------------------------------

function startPropertyAction(state: GameState): ApplyResult {
  return ok({ ...state, property: { ...state.property, active: true } });
}
function propertyBuyAction(state: GameState, slot: number): ApplyResult {
  if (!state.property.active) return fail('NOT_STARTED', 'Open the property board first.');
  if (state.property.portfolio.length >= MAX_PROPERTIES) {
    return fail('PORTFOLIO_FULL', `You can only own ${MAX_PROPERTIES} properties — sell one first.`);
  }
  const week = state.clock.week;
  const listings = generateListings(state.rngSeed, week);
  const listing = listings.find((l) => l.slot === slot);
  if (!listing) return fail('NO_SUCH_LISTING', 'That property is not listed this week.');

  const boughtThisWeek = state.property.boughtSlotsWeek === week ? state.property.boughtSlots : [];
  if (boughtThisWeek.includes(slot)) return fail('ALREADY_BOUGHT', 'You already bought that one.');
  if (state.wallet.cash < listing.buyPrice) {
    return fail('INSUFFICIENT_FUNDS', `You need B$${listing.buyPrice} to buy the ${listing.name}.`);
  }

  let s = applyTxn(state, {
    type: 'property_buy',
    amount: -listing.buyPrice,
    category: 'expense',
    note: `Bought ${listing.name} for B$${listing.buyPrice}`,
  });
  s = {
    ...s,
    property: {
      ...s.property,
      portfolio: [
        ...s.property.portfolio,
        {
          id: `prop-${week}-${slot}`,
          defId: listing.defId,
          name: listing.name,
          emoji: listing.emoji,
          buyPrice: listing.buyPrice,
          fairValue: listing.fairValue,
          spread: listing.spread,
          renovations: 0,
          rented: false,
          boughtWeek: week,
        },
      ],
      boughtSlots: [...boughtThisWeek, slot],
      boughtSlotsWeek: week,
    },
  };
  return ok(s, [{ kind: 'CoinsSpent', amount: listing.buyPrice, reason: `Bought ${listing.name}` }]);
}
function propertyRenovateAction(state: GameState, propertyId: string): ApplyResult {
  if (!state.property.active) return fail('NOT_STARTED', 'Open the property board first.');
  const prop = state.property.portfolio.find((p) => p.id === propertyId);
  if (!prop) return fail('NOT_FOUND', 'You do not own that property.');
  if (prop.renovations >= MAX_RENOVATIONS) {
    return fail('MAXED_OUT', 'That property is fully renovated.');
  }
  if (prop.rented) return fail('TENANTED', 'A tenant lives there — you cannot renovate now.');
  const cost = renovationCost(prop);
  if (state.wallet.cash < cost) {
    return fail('INSUFFICIENT_FUNDS', `The renovation costs B$${cost}.`);
  }
  let s = applyTxn(state, {
    type: 'property_reno',
    amount: -cost,
    category: 'expense',
    note: `Renovated ${prop.name} (level ${prop.renovations + 1})`,
  });
  s = {
    ...s,
    property: {
      ...s.property,
      portfolio: s.property.portfolio.map((p) =>
        p.id === propertyId ? { ...p, renovations: p.renovations + 1 } : p,
      ),
    },
  };
  return ok(s, [{ kind: 'CoinsSpent', amount: cost, reason: `Renovated ${prop.name}` }]);
}
function propertyRentAction(state: GameState, propertyId: string): ApplyResult {
  if (!state.property.active) return fail('NOT_STARTED', 'Open the property board first.');
  const prop = state.property.portfolio.find((p) => p.id === propertyId);
  if (!prop) return fail('NOT_FOUND', 'You do not own that property.');
  if (prop.rented) return fail('ALREADY_RENTED', 'A tenant already lives there.');
  return ok({
    ...state,
    property: {
      ...state.property,
      portfolio: state.property.portfolio.map((p) =>
        p.id === propertyId ? { ...p, rented: true } : p,
      ),
    },
  });
}
function propertySellAction(state: GameState, propertyId: string): ApplyResult {
  if (!state.property.active) return fail('NOT_STARTED', 'Open the property board first.');
  const prop = state.property.portfolio.find((p) => p.id === propertyId);
  if (!prop) return fail('NOT_FOUND', 'You do not own that property.');
  const week = state.clock.week;
  // Patience rule, same as trade: no same-week flips.
  if (prop.boughtWeek >= week) return fail('TOO_SOON', 'Hold it at least a week before selling.');

  const condition = marketCondition(state.rngSeed, week);
  const value = saleValue(prop, condition);
  let s = applyTxn(state, {
    type: 'property_sale',
    amount: value,
    category: 'active',
    note: `Sold ${prop.name} for B$${value} (paid B$${prop.buyPrice}, value B$${propertyValue(prop)})`,
  });
  s = {
    ...s,
    property: { ...s.property, portfolio: s.property.portfolio.filter((p) => p.id !== propertyId) },
  };
  return ok(s, [{ kind: 'CoinsEarned', amount: value, from: `Sold ${prop.name}` }]);
}

// ---------- broker -----------------------------------------------------------

function startBrokerAction(state: GameState): ApplyResult {
  return ok({ ...state, broker: { ...state.broker, active: true } });
}
function brokerWorkAction(state: GameState, slot: number): ApplyResult {
  if (!state.broker.active) return fail('NOT_STARTED', 'Open the deal desk first.');
  const week = state.clock.week;
  if (state.broker.workingWeek === week && state.broker.workingSlot !== null) {
    return fail('ALREADY_WORKING', 'You are already working a deal this week.');
  }
  const deals = generateDeals(state.rngSeed, week);
  if (!deals.some((d) => d.slot === slot)) {
    return fail('NO_SUCH_DEAL', 'That deal is not on the board this week.');
  }
  if (state.profile.happiness < WORK_ENERGY) {
    return fail('LOW_ENERGY', 'Too tired to work a deal — take a break first.');
  }
  // Energy is spent up front (the calls and legwork happen either way); the
  // deal itself resolves at settle against the shown close chance.
  return ok({
    ...state,
    profile: { ...state.profile, happiness: Math.max(0, state.profile.happiness - WORK_ENERGY) },
    broker: { ...state.broker, workingSlot: slot, workingWeek: week },
  });
}

function setDreamGoalAction(state: GameState, goalId: string, target: number): ApplyResult {
  if (target <= 0) return fail('INVALID_TARGET', 'Goal target must be > 0.');
  return ok({
    ...state,
    dream: { goalId, fundedAmount: 0, targetAmount: target },
  });
}

function advanceDayAction(
  state: GameState,
  dayKey: string,
  content: BuilderContent,
): ApplyResult {
  const outbound: OutboundEvent[] = [];
  let s = state;

  // Day 5 → settle, then roll to next week's day 1.
  if (s.clock.dayOfWeek === 5) {
    const settled = settleWeek(s, content);
    s = settled.state;
    outbound.push(...settled.outbound);
    // settleWeek advanced week and reset dayOfWeek to 1.
  } else {
    s = {
      ...s,
      clock: { ...s.clock, dayOfWeek: ((s.clock.dayOfWeek + 1) as 1 | 2 | 3 | 4 | 5) },
    };
  }

  // Refill AP for the new day.
  s = refillAP(s);

  // Record wall-clock day key for catch-up logic.
  s = {
    ...s,
    clock: { ...s.clock, lastWallClockDayKey: dayKey },
  };

  outbound.push({ kind: 'DayCaughtUp', dayOfWeek: s.clock.dayOfWeek, week: s.clock.week });
  // Update cashflow meter as a side effect of the day rolling.
  outbound.push({
    kind: 'MeterMoved',
    cashflowDelta: cashflowForWeek(s, s.clock.week),
  });
  return ok(s, outbound);
}

// ---------- dispatcher -----------------------------------------------------

export function apply(state: GameState, action: Action, content: BuilderContent): ApplyResult {
  switch (action.kind) {
    case 'CompleteWakeUp':
      return wakeUpAction(state, action.dayKey, action.mindset);
    case 'StartBusiness':
      return startBusiness(state, action.defId, content);
    case 'SetPrice':
      return setPrice(state, action.businessId, action.price);
    case 'SpendMarketing':
      return spendMarketing(state, action.businessId, action.amount);
    case 'RegisterBusinessAsAsset':
      return registerBusinessAsAsset(state, action.businessId, content);
    case 'BuyAsset':
      return buyAssetAction(state, action.defId, content);
    case 'BuyLiability':
      return buyLiabilityAction(state, action.defId, content);
    case 'TakeLoan':
      return takeLoanAction(
        state,
        action.termWeeks === undefined
          ? { principal: action.principal }
          : { principal: action.principal, termWeeks: action.termWeeks },
        content,
      );
    case 'Deposit':
      return depositAction(state, action.amount);
    case 'Withdraw':
      return withdrawAction(state, action.amount);
    case 'Give':
      return giveAction(state, action.amount, action.causeId);
    case 'AcceptInvestment':
      return acceptInvestmentAction(state, action.amount, action.sharePct);
    case 'PushHard':
      return pushHardAction(state);
    case 'TakeBreak':
      return takeBreakAction(state);
    case 'ResolveDecision':
      return resolveDecisionAction(state, action);
    case 'CompleteReflect':
      return reflectAction(state, action.entry, action.tags);
    case 'SetDreamGoal':
      return setDreamGoalAction(state, action.goalId, action.targetAmount);
    case 'ResolveDecisionCard':
      return resolveDecisionCardAction(state, action);
    case 'AckConsequence':
      return ackConsequenceAction(state, action.id);
    case 'SetResilience':
      return ok({
        ...state,
        inSlump: action.inSlump,
        lastResilienceWeek: Math.max(state.lastResilienceWeek ?? 0, action.week),
      });
    case 'StartServices':
      return startServicesAction(state);
    case 'SetServiceRate':
      return setServiceRateAction(state, action.rate);
    case 'SetServiceHours':
      return setServiceHoursAction(state, action.hours);
    case 'StartSocial':
      return startSocialAction(state);
    case 'PostContent':
      return postContentAction(state);
    case 'StartGaming':
      return startGamingAction(state);
    case 'SetGamingAction':
      return setGamingActionAction(state, action.action);
    case 'StartTrade':
      return startTradeAction(state);
    case 'TradeBuy':
      return tradeBuyAction(state, action.slot);
    case 'TradeSell':
      return tradeSellAction(state, action.itemId);
    case 'StartProperty':
      return startPropertyAction(state);
    case 'PropertyBuy':
      return propertyBuyAction(state, action.slot);
    case 'PropertyRenovate':
      return propertyRenovateAction(state, action.propertyId);
    case 'PropertyRent':
      return propertyRentAction(state, action.propertyId);
    case 'PropertySell':
      return propertySellAction(state, action.propertyId);
    case 'StartBroker':
      return startBrokerAction(state);
    case 'BrokerWork':
      return brokerWorkAction(state, action.slot);
    case 'AdvanceDay':
      return advanceDayAction(state, action.dayKey, content);
  }
}

/** Apply a sequence of actions in order, short-circuiting on first failure. */
export function applyMany(
  state: GameState,
  actions: readonly Action[],
  content: BuilderContent,
): ApplyResult {
  let s = state;
  const outbound: OutboundEvent[] = [];
  for (const a of actions) {
    const r = apply(s, a, content);
    if (!r.ok) return r;
    s = r.value.state;
    outbound.push(...r.value.outbound);
  }
  return ok(s, outbound);
}
